<script lang="ts" setup>
import { Coupon, Item } from '@/@types/cart'
import Image from '@/assets/show/image.webp'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { useCartTotalsStore } from '@/composables/useCartTotalsStore'
import { currentPriceFormat, formatPrice } from '@/helpers/current-price'
import { findTicketLot } from '@/helpers/ticket-batch'
import { cn } from '@/lib/utils'
import { FormSchema } from '@/pages/carts/show.vue'
import { Loader2 } from 'lucide-vue-next'
import { inject, toRefs } from 'vue'

const props = withDefaults(
    defineProps<{
        cartItems: Item[]
        coupon?: Coupon
        hasAgreed?: boolean
        form?: FormSchema
        class?: string
    }>(),
    {
        hasAgreed: true,
    },
)

const { currentStep, agreed, controlStep, sessionCart } = inject<{
    currentStep: number
    agreed: boolean
    controlStep: boolean
    sessionCart?: string
}>('stepControl', {
    currentStep: 1,
    agreed: false,
    controlStep: false,
    sessionCart: '',
})

const lastStep = 4
const firstStep = 1

const { cartItems, coupon } = toRefs(props)

const { standardCouponApplies, subtotal, total, installmentTotals, discount } = useCartTotalsStore()
</script>

<template>
    <Card :class="cn('font-inter sticky top-0 h-fit w-full max-w-96 border-slate-200 bg-white', props.class)">
        <CardHeader>
            <CardTitle class="text-lg font-medium">
                {{ $t('Order summary') }}
            </CardTitle>
        </CardHeader>
        <CardContent class="flex flex-col gap-x-4 gap-y-6">
            <div v-for="item in cartItems" :key="item.id" class="flex gap-4 border-b pb-4">
                <img :src="Image" alt="Ingresso" class="h-[78px] w-[72px] rounded-md object-cover" />
                <div class="flex w-full flex-col gap-1">
                    <p class="text-base font-normal">{{ $t('Ticket type') }}</p>
                    <div class="flex items-center justify-between gap-2">
                        <p class="font-bold">{{ $t(item.ticket.category) }}</p>
                        <p>{{ item.quantity }}x</p>
                    </div>
                    <div class="flex items-center justify-between gap-2">
                        <p class="text-sm">{{ $t('Batch') }} {{ findTicketLot(item.ticket.promotions) + 1 }}</p>
                        <p class="font-bold">
                            {{ currentPriceFormat(item.ticket) }}
                        </p>
                    </div>
                </div>
            </div>
            <div class="flex w-full flex-col gap-[8px] border-b pb-6">
                <div class="flex w-full items-center justify-between">
                    <p class="text-sm text-slate-600">{{ $t('Subtotal') }}:</p>
                    <p class="text-sm font-semibold text-slate-600">{{ formatPrice(subtotal) }}</p>
                </div>
                <div class="flex w-full items-center justify-between" v-if="standardCouponApplies">
                    <p class="text-sm text-slate-600">{{ $t('Discounts') }}:</p>
                    <p class="text-sm font-semibold text-green-600">10% (-{{ formatPrice(discount) }})</p>
                </div>
                <div class="flex w-full items-center justify-between" v-if="coupon">
                    <p class="text-sm text-slate-600">{{ $t('Coupon') }}:</p>
                    <p class="text-sm font-semibold text-green-600" v-if="coupon.category == 'currency'">
                        - {{ formatPrice(coupon.value_cents / 100) }}
                    </p>

                    <p class="text-sm font-semibold text-green-600" v-if="coupon.category == 'percentage' && !standardCouponApplies">
                        {{ coupon.percentage_value }}% (- {{ formatPrice(subtotal * (coupon.percentage_value / 100)) }})
                    </p>
                </div>
            </div>
            <div v-if="form?.payment_type === 'credit_card' && form.installments && (installmentTotals || total)" class="flex flex-col gap-6">
                <div class="flex justify-between text-sm">
                    <p class="font-semibold text-slate-800">{{ $t('You will pay') }}:</p>
                    <p class="font-semibold text-slate-800">
                        {{ form?.installments }}x {{ formatPrice((installmentTotals || total) / Number(form?.installments)) }}
                    </p>
                </div>
            </div>
            <div class="flex flex-col gap-6">
                <div class="flex justify-between">
                    <p class="text-lg font-semibold text-slate-800">{{ $t('Total') }}:</p>
                    <p class="text-lg font-semibold text-slate-800">
                        {{ form?.payment_type === 'credit_card' && installmentTotals ? formatPrice(installmentTotals) : formatPrice(total) }}
                    </p>
                </div>
            </div>
        </CardContent>
        <CardFooter class="flex flex-col gap-6" v-if="hasAgreed">
            <div class="flex flex-col gap-6">
                <div class="flex flex-col gap-4">
                    <div class="flex gap-2">
                        <Checkbox v-model="agreed" :disabled="currentStep !== firstStep" />
                        <p class="text-xs text-slate-500">
                            {{ $t('By continuing, you agree to the') }}
                            <a href="/privacy-policy" class="underline" target="_blank"> {{ $t('General Terms') }} </a>,
                            <a href="/privacy-policy#cancellation" class="underline" target="_blank">
                                {{ $t('Cancellation Policy') }}
                            </a>
                            {{ $t('and the') }}
                            <a href="/privacy-policy#privacy" class="underline" target="_blank"> {{ $t('Privacy Policy') }} </a>.
                        </p>
                    </div>
                </div>
            </div>

            <Button
                class="sticky w-full lg:relative"
                form="form"
                :disabled="((!controlStep || !agreed) && (!sessionCart || !agreed)) || form?.processing"
            >
                <template v-if="!form?.processing">
                    {{ currentStep === lastStep ? $t('Confirm and Make Payment') : $t('Next') }}
                </template>
                <template v-else>
                    <Loader2 class="animate-spin" />
                    <span class="sr-only">{{ $t('Creating order and making payment') }}</span>
                </template>
            </Button>
        </CardFooter>
    </Card>
</template>
